import React, { useState, useEffect, useRef } from 'react';
import { 
  FileCheck, Upload, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Edit3, Trash2, CheckCircle2, AlertTriangle, Info, Sparkles,
  Printer, FilePlus, ShieldCheck
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import { LocalModelToggle } from './LocalModelToggle';

interface DelovodstvoAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface Violation {
  requisiteOrRule: string;
  standard: string;
  asIs: string;
  shouldBe: string;
  severity: 'critical' | 'warning' | 'info';
  explanation: string;
}

interface AuditResult {
  complianceScore: number;
  documentTypeDetected: string;
  summary: string;
  violations: Violation[];
  fullAuditReportMarkdown: string;
  correctedDocumentMarkdown: string;
}

const LOADING_STEPS_UK = [
  "Завантаження та первинний аналіз структури документа...",
  "Перевірка 32 обов'язкових реквізитів за ДСТУ 4163:2020 та ПКМУ № 55...",
  "Аудит офіційно-ділового стилю, правопису 2019 р. та підписантів...",
  "Генерація виправленого тексту документа та структурованого звіту..."
];

const LOADING_STEPS_EN = [
  "Loading and initial document structure analysis...",
  "Auditing mandatory metadata against DSTU 4163:2020 & Resolution No. 55...",
  "Reviewing professional terminology, spelling rules, and signees...",
  "Generating compliant document text and final audit report..."
];

const LOADING_STEPS_PL = [
  "Ładowanie i wstępna analiza struktury dokumentu...",
  "Weryfikacja metadanych i zgodności z normami kancelaryjnymi ДСТУ...",
  "Audyt stylu urzędowego, podpisów i struktury prawnej...",
  "Generowanie poprawionego dokumentu oraz końcowego raportu z audytu..."
];

export default function DelovodstvoAuditor({ 
  language, 
  onBack, onGoHome, 
  useEnterpriseContext: initialEnterpriseContext, 
  enterpriseName 
}: DelovodstvoAuditorProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const [text, setText] = useState('');
  const [documentType, setDocumentType] = useState('auto');
  const [useEnterprise, setUseEnterprise] = useState(!!initialEnterpriseContext);
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : false;
  });
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);

  const [isExtracting, setIsExtracting] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [activeTab, setActiveTab] = useState<'violations' | 'report' | 'corrected'>('violations');

  // Cycle loading steps
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : isPl ? LOADING_STEPS_PL : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 4000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk, isPl]);

  useEffect(() => {
    if (auditResult && auditResult.fullAuditReportMarkdown) {
      autoDownloadReport(auditResult.fullAuditReportMarkdown, isUk ? 'Аудит Діловодства ДСТУ 4163' : 'DSTU 4163 Document Audit', 'delovodstvo-auditor');
    }
  }, [auditResult, isUk]);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');
  const [copiedText, setCopiedText] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File Upload and Text Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize(file.size);
    setError(null);
    setIsExtracting(true);
    setAuditResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Помилка вилучення тексту з файлу.');
      }

      const data = await response.json();
      setText(data.text);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Не вдалося прочитати файл.');
      setFileName(null);
      setFileSize(null);
    } finally {
      setIsExtracting(false);
    }
  };

  // Run Delovodstvo Audit (DSTU 4163 & PKMU 55)
  const handleRunAudit = async () => {
    if (!text.trim()) return;

    setIsAuditing(true);
    setError(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/audit-delovodstvo', {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          text, 
          documentType, 
          useEnterpriseContext: useEnterprise,
          useLocalModel
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Помилка під час аудиту діловодства.');
      }

      const data: AuditResult = await response.json();
      setAuditResult(data);
      setActiveTab('violations');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Сталася помилка при зверненні до сервісу аудиту.');
    } finally {
      setIsAuditing(false);
    }
  };

  // Download Corrected Document as Word (.docx)
  const handleDownloadDocx = async () => {
    if (!auditResult?.correctedDocumentMarkdown) return;
    setIsDownloadingDocx(true);

    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: auditResult.correctedDocumentMarkdown,
          fileName: `Normative_Doc_DSTU4163_${fileName ? fileName.replace(/\.[^/.]+$/, '') : 'audit'}`
        })
      });

      if (!response.ok) {
        throw new Error('Помилка при генерації файлу Word (.docx).');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName ? fileName.replace(/\.[^/.]+$/, '') : 'document'}_DSTU4163_compliant.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Не вдалося завантажити Word файл.');
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  // Download PDF Report
  const handleDownloadPdf = async () => {
    if (!auditResult) return;
    setIsDownloadingPdf(true);

    try {
      const content = activeTab === 'corrected' 
        ? auditResult.correctedDocumentMarkdown 
        : auditResult.fullAuditReportMarkdown;

      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: content,
          title: `Аудит діловодства ДСТУ 4163:2020 & ПКМУ № 55`
        })
      });

      if (!response.ok) {
        throw new Error('Помилка при генерації PDF.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Delovodstvo_Audit_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Не вдалося завантажити PDF.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Copy Corrected Text to Clipboard
  const handleCopyText = () => {
    if (!auditResult?.correctedDocumentMarkdown) return;
    navigator.clipboard.writeText(auditResult.correctedDocumentMarkdown);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2500);
  };

  // Filtered Violations
  const filteredViolations = auditResult?.violations.filter(v => {
    if (severityFilter === 'all') return true;
    return v.severity === severityFilter;
  }) || [];

  const criticalCount = auditResult?.violations.filter(v => v.severity === 'critical').length || 0;
  const warningCount = auditResult?.violations.filter(v => v.severity === 'warning').length || 0;
  const infoCount = auditResult?.violations.filter(v => v.severity === 'info').length || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4 bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-xl text-white">
        <div className="flex items-center gap-3 flex-wrap">
          <button 
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            🏠 {isUk ? "На головну" : "Home"}
          </button>
          <button 
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isUk ? "← До Офісного Центру" : "← Back to Office Hub"}</span>
          </button>

          <div className="flex items-center space-x-3 ml-2">
            <div className="p-2.5 bg-indigo-500/20 border border-indigo-500/30 rounded-xl text-indigo-400">
              <FileCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-white via-slate-100 to-indigo-200 bg-clip-text text-transparent">
                  Аудитор діловодства (ДСТУ 4163 & ПКМУ № 55)
                </h1>
                <span className="px-2.5 py-0.5 text-xs font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Gemini 3.7 Flash
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Аналіз відповідності ДСТУ 4163:2020 та ПКМУ № 55, виправлення помилок та експорт у Word (.docx)
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />

          {auditResult && (
            <button
              onClick={() => setAuditResult(null)}
              className="flex items-center space-x-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-indigo-600/20"
            >
              <FilePlus className="w-4 h-4" />
              <span>Новий аудит</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {!auditResult ? (
        <div className="space-y-6">
          {/* Upload Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl text-slate-100">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-400" />
              <span>Завантаження документа для перевірки</span>
            </h2>

            {/* Drop Zone */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-700 hover:border-indigo-500/50 bg-slate-800/40 hover:bg-slate-800/80 rounded-xl p-8 text-center cursor-pointer transition-all duration-200 group"
            >
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileUpload} 
                accept=".docx,.doc,.pdf,.txt" 
                className="hidden" 
              />
              <div className="flex flex-col items-center">
                <div className="p-4 bg-indigo-500/10 text-indigo-400 group-hover:bg-indigo-500/20 rounded-2xl mb-4 transition-all border border-indigo-500/20">
                  {isExtracting ? (
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-400" />
                  ) : (
                    <Upload className="w-8 h-8 text-indigo-400" />
                  )}
                </div>
                <h3 className="text-base font-semibold text-white mb-1">
                  {isExtracting ? 'Вилучення тексту з документа...' : 'Натисніть або перетягніть файл сюди'}
                </h3>
                <p className="text-sm text-slate-400 max-w-md">
                  Підтримуються файли <strong>Word (.docx, .doc)</strong>, <strong>PDF (.pdf)</strong> та <strong>TXT (.txt)</strong>
                </p>
                {fileName && (
                  <div className="mt-4 px-3 py-1.5 bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-mono rounded-lg flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span>{fileName} ({Math.round((fileSize || 0) / 1024)} KB)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Demo Sample Button */}
            <button
              type="button"
              onClick={() => {
                setFileName(isUk ? "sample_nakaz_dstu.docx" : isPl ? "przyklad_zarzadzenie.docx" : "sample_official_order.docx");
                setFileSize(15400);
                if (isUk) {
                  setText(`МІНІСТЕРСТВО ОСВІТИ І НАУКИ УКРАЇНИ
НАКАЗ
12 січня 2026 року          м.Київ          № 14-к

Про притягнення до дисциплінарної відповідальності

У зв'язку з неналежним виконанням службових обов'язків Сидоренко О.В.

НАКАЗУЮ:

1. Оголосити догану старшому спеціалісту Сидоренку Олександру Володимировичу.
2. Контроль за виконанням даного наказу залишаю за собою.

Міністр                 (підпис)              Іван ПЕТРЕНКО`);
                  setDocumentType("Наказ з основної діяльності");
                } else if (isPl) {
                  setText(`MINISTERSTWO EDUKACJI I NAUKI
ZARZĄDZENIE
12 stycznia 2026 roku          Warszawa          Nr 14-k

W sprawie pociągnięcia do odpowiedzialności dyscyplinarnej

W związku z nienależytym wykonywaniem obowiązków służbowych przez Sydorenko O.W.

ZARZĄDZAM:

1. Udzielić nagany starszemu specjaliście Aleksandrowi Sydorenko.
2. Kontrolę nad wykonaniem niniejszego zarządzenia zastrzegam dla siebie.

Minister                 (podpis)              Jan PETRENKO`);
                  setDocumentType("Розпорядження");
                } else {
                  setText(`MINISTRY OF EDUCATION AND SCIENCE OF UKRAINE
ORDER
12 January 2026          Kyiv          No. 14-k

On Disciplinary Action

In connection with improper performance of official duties by Sydorenko O.V.

HEREBY ORDER:

1. To issue a reprimand to Senior Specialist Oleksandr Sydorenko.
2. I reserve control over the execution of this order.

Minister                 (signature)              Ivan PETRENKO`);
                  setDocumentType("Наказ з основної діяльності");
                }
              }}
              className="w-full mt-4 py-2.5 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-xl text-emerald-300 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
            >
              <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span>
                {isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Перевірка Наказу за ДСТУ 4163)" :
                 isPl ? "⚡ Wypróbuj przykład demo w 1 kliknięcie (Weryfikacja zarządzenia DSTU 4163)" :
                 "⚡ Try 1-Click Demo Sample (DSTU 4163 Order Audit)"}
              </span>
            </button>

            {/* Document Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-2">
                  Тип документа (для точного аудиту реквізитів)
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500 transition-all"
                >
                  <option value="auto">✨ Автоматичне визначення типом ШІ</option>
                  <option value="Наказ з основної діяльності">Наказ (з основної діяльності)</option>
                  <option value="Наказ з кадрових питань">Наказ (з кадрових питань)</option>
                  <option value="Розпорядження">Розпорядження</option>
                  <option value="Офіційний лист (вихідний)">Офіційний лист (вихідний)</option>
                  <option value="Акт">Акт (прийому-передачі / обстеження тощо)</option>
                  <option value="Протокол">Протокол засідання / наради</option>
                  <option value="Службова / Доповідна записка">Службова / Доповідна записка</option>
                  <option value="Інший документ">Інший управлінський документ</option>
                </select>
              </div>

              {enterpriseName && (
                <div className="flex items-center space-x-3 pt-6">
                  <input
                    type="checkbox"
                    id="useEnterpriseContext"
                    checked={useEnterprise}
                    onChange={(e) => setUseEnterprise(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 bg-slate-800 border-slate-700 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="useEnterpriseContext" className="text-sm text-slate-300 cursor-pointer">
                    Враховувати реквізити та базу знань підприємства (<strong>{enterpriseName}</strong>)
                  </label>
                </div>
              )}
            </div>

            {/* Text Editor Area */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-xs font-semibold uppercase text-slate-400">
                  Текст документа (зможете відредагувати перед аудитом)
                </label>
                {text && (
                  <button 
                    onClick={() => { setText(''); setFileName(null); }}
                    className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Очистити
                  </button>
                )}
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Вставте текст документа сюди або завантажте файл Word/PDF..."
                rows={12}
                className="w-full bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 rounded-xl p-4 text-sm font-mono focus:outline-none focus:border-indigo-500 transition-all"
              />
            </div>

            {error && (
              <div className="mt-4 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-xl text-sm flex items-center gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-400" />
                <span>{error}</span>
              </div>
            )}

            {/* Live Progress Bar when Auditing */}
            {isAuditing && (
              <div className="mt-6 p-5 bg-indigo-950/40 border border-indigo-500/30 rounded-2xl animate-pulse">
                <div className="flex items-center gap-3 text-indigo-300 text-sm font-medium mb-3">
                  <RefreshCw className="w-5 h-5 animate-spin text-indigo-400" />
                  <span>
                    {(isUk ? LOADING_STEPS_UK : isPl ? LOADING_STEPS_PL : LOADING_STEPS_EN)[loadingStepIndex]}
                  </span>
                </div>
                <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 to-emerald-400 h-full transition-all duration-500"
                    style={{ width: `${((loadingStepIndex + 1) / 4) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action Submit */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleRunAudit}
                disabled={!text.trim() || isAuditing || isExtracting}
                className="px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isAuditing ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Триває перевірка за ДСТУ 4163 & ПКМУ № 55...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    <span>Розпочати аудит діловодства</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Audit Results Section */
        <div className="space-y-6">
          {/* Top Score Banner */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl text-white">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
              {/* Score Gauge */}
              <div className="flex flex-col items-center justify-center p-4 bg-slate-950/60 rounded-xl border border-slate-800">
                <div className="text-xs uppercase font-semibold text-slate-400 mb-1">Оцінка відповідності</div>
                <div className={`text-4xl font-extrabold ${
                  auditResult.complianceScore >= 85 ? 'text-emerald-400' :
                  auditResult.complianceScore >= 60 ? 'text-amber-400' : 'text-rose-400'
                }`}>
                  {auditResult.complianceScore}%
                </div>
                <div className="text-xs text-slate-500 mt-1">ДСТУ 4163 & ПКМУ № 55</div>
              </div>

              {/* Summary Info */}
              <div className="md:col-span-2 space-y-2">
                <div className="inline-block px-3 py-1 bg-indigo-500/10 text-indigo-300 text-xs font-semibold rounded-lg border border-indigo-500/20">
                  Тип: {auditResult.documentTypeDetected}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {auditResult.summary}
                </p>
              </div>

              {/* Stats badges */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center px-3 py-2 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-xs">
                  <span className="flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Критичні помилки:</span>
                  <span className="font-bold">{criticalCount}</span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-lg text-xs">
                  <span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Застереження:</span>
                  <span className="font-bold">{warningCount}</span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-lg text-xs">
                  <span className="flex items-center gap-1.5"><Info className="w-4 h-4" /> Інфо / Рекомендації:</span>
                  <span className="font-bold">{infoCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Controls & Toolbar */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl flex flex-wrap items-center justify-between gap-4">
            {/* View Tabs */}
            <div className="flex items-center space-x-2 bg-slate-950 p-1.5 rounded-xl border border-slate-800">
              <button
                onClick={() => setActiveTab('violations')}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                  activeTab === 'violations' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                <span>Таблиця порушень ({auditResult.violations.length})</span>
              </button>

              <button
                onClick={() => setActiveTab('corrected')}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                  activeTab === 'corrected' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Виправлений документ (Word)</span>
              </button>

              <button
                onClick={() => setActiveTab('report')}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                  activeTab === 'report' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Повний Звіт</span>
              </button>
            </div>

            {/* Export Actions */}
            <div className="flex items-center space-x-3 flex-wrap gap-y-2">
              <TelegramExportButton
                title={fileName ? `Аудит діловодства: ${fileName}` : 'Аудит діловодства (ДСТУ 4163:2020)'}
                content={auditResult ? (auditResult.correctedDocumentMarkdown || auditResult.fullAuditReportMarkdown) : ''}
                agentName="Аудитор Діловодства (ДСТУ 4163 & ПКМУ №55)"
                agentId="dstu-pkmu-auditor"
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2"
                title="Завантажити виправлений нормативний документ у MS Word"
              >
                {isDownloadingDocx ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                <span>Завантажити Word (.docx)</span>
              </button>

              <button
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
                title="Друк або збереження у PDF"
              >
                {isDownloadingPdf ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                <span>Друк / PDF</span>
              </button>

              <button
                onClick={handleCopyText}
                className="px-3 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-1.5"
                title="Скопіювати виправлений текст"
              >
                {copiedText ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copiedText ? 'Скопійовано' : 'Копіювати'}</span>
              </button>
            </div>
          </div>

          {/* Tab 1: Violations Table */}
          {activeTab === 'violations' && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl text-slate-100 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-4">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-indigo-400" />
                  <span>Деталізований реєстр відхилень від ДСТУ 4163 & ПКМУ № 55</span>
                </h3>

                {/* Filter Selector */}
                <div className="flex items-center space-x-1 bg-slate-950 p-1 rounded-lg border border-slate-800">
                  <button
                    onClick={() => setSeverityFilter('all')}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${
                      severityFilter === 'all' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Усі ({auditResult.violations.length})
                  </button>
                  <button
                    onClick={() => setSeverityFilter('critical')}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${
                      severityFilter === 'critical' ? 'bg-rose-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Критичні ({criticalCount})
                  </button>
                  <button
                    onClick={() => setSeverityFilter('warning')}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${
                      severityFilter === 'warning' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Застереження ({warningCount})
                  </button>
                  <button
                    onClick={() => setSeverityFilter('info')}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${
                      severityFilter === 'info' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Інфо ({infoCount})
                  </button>
                </div>
              </div>

              {/* Table */}
              {filteredViolations.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3 opacity-80" />
                  <p className="text-base font-semibold text-white">Відхилень даного типу не виявлено!</p>
                  <p className="text-sm text-slate-500">Документ відповідає зазначеним параметрам діловодства.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950 text-slate-400 text-xs uppercase font-semibold">
                        <th className="py-3 px-4">Реквізит / Правило</th>
                        <th className="py-3 px-4">Норма</th>
                        <th className="py-3 px-4">Знайдено в документі</th>
                        <th className="py-3 px-4">Як має бути за стандартом</th>
                        <th className="py-3 px-4">Пояснення</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60 text-slate-300">
                      {filteredViolations.map((v, i) => (
                        <tr key={i} className="hover:bg-slate-800/40 transition-all">
                          <td className="py-3.5 px-4 font-semibold text-white">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${
                                v.severity === 'critical' ? 'bg-rose-500' :
                                v.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                              }`} />
                              <span>{v.requisiteOrRule}</span>
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-xs font-mono text-indigo-300">
                            {v.standard}
                          </td>
                          <td className="py-3.5 px-4 text-xs font-mono text-rose-300 bg-rose-500/5 rounded p-2 border border-rose-500/10">
                            {v.asIs}
                          </td>
                          <td className="py-3.5 px-4 text-xs font-mono text-emerald-300 bg-emerald-500/5 rounded p-2 border border-emerald-500/10">
                            {v.shouldBe}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-400">
                            {v.explanation}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Corrected Document View */}
          {activeTab === 'corrected' && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl text-slate-100">
              <div className="flex justify-between items-center border-b border-slate-800 pb-4 mb-4">
                <div>
                  <h3 className="text-base font-semibold text-white flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span>Повністю виправлений нормативний документ</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Документ оформлено відповідно до вимог ДСТУ 4163:2020 та ПКМУ № 55
                  </p>
                </div>
                <button
                  onClick={handleDownloadDocx}
                  disabled={isDownloadingDocx}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 shadow-md"
                >
                  <Download className="w-4 h-4" />
                  <span>Завантажити Word (.docx)</span>
                </button>
              </div>

              <div className="bg-white text-slate-900 rounded-xl p-8 shadow-inner font-sans leading-relaxed text-sm overflow-x-auto min-h-[500px]">
                <pre className="whitespace-pre-wrap font-serif text-base text-slate-900 border-none bg-transparent">
                  {auditResult.correctedDocumentMarkdown}
                </pre>
              </div>
            </div>
          )}

          {/* Tab 3: Full Report View */}
          {activeTab === 'report' && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl text-slate-100 space-y-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2 border-b border-slate-800 pb-4">
                <FileText className="w-5 h-5 text-indigo-400" />
                <span>Повний текстовий Звіт аудиту діловодства</span>
              </h3>
              <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 text-slate-300 font-mono text-xs whitespace-pre-wrap leading-relaxed overflow-x-auto">
                {auditResult.fullAuditReportMarkdown}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
